/**
 * Work Log Review — frontend response shape.
 *
 * Mirrors the wire contract returned by the 159a backend route
 * (`GET /api/v1/orgs/:orgId/work-log-review`). When the backend shape
 * changes, this file and the matching Zod schema in
 * `src/data/schemas/workLogReview.ts` change with it.
 */

export type GroupByDimension =
  | 'app'
  | 'appFileName'
  | 'title'
  | 'rootDomain'
  | 'site'
  | 'url'
  | 'employee'
  | 'businessUnit'
  | 'project'
  | 'client'
  | 'classificationSource'
  | 'classifiedByRule'
  | 'day'
  | 'hour'
  | 'weekday';

export const GROUP_BY_DIMENSIONS: readonly GroupByDimension[] = [
  'app',
  'appFileName',
  'title',
  'rootDomain',
  'site',
  'url',
  'employee',
  'businessUnit',
  'project',
  'client',
  'classificationSource',
  'classifiedByRule',
  'day',
  'hour',
  'weekday',
] as const;

/** Classification source for an event's `classified_event` row.
 *
 *   - `rule`      — matched a saved classification rule
 *   - `manual`    — set explicitly by a user
 *   - `fallback`  — internal default (the "unclassified" state)
 *   - `ai_text`   — auto-suggested by the text-pass model (task 202)
 *   - `ai_vision` — auto-suggested by the vision-pass model (task 203)
 *   - `ai`        — legacy combined value retained for older payloads /
 *                   fixtures; new writers emit `ai_text` or `ai_vision`.
 *
 *  Display labels NEVER expose these raw enum values — see SOURCE_LABELS
 *  in WorkLogReviewFilters / WorkLogReviewEventLog for the human-facing
 *  text. Per task 204 D9.
 */
export type ClassificationSource = 'rule' | 'fallback' | 'manual' | 'ai' | 'ai_text' | 'ai_vision';

export type SortByColumn =
  | 'count'
  | 'hours'
  | 'distinctEmployees'
  | 'firstSeenAt'
  | 'lastSeenAt'
  | 'percentOfFiltered'
  /** Task 204 D9 — sort by the row's aggregate fused confidence score.
   *  Descending = high-confidence-first. Backend computes the per-group
   *  average from `event_classification_suggestion.fused`. */
  | 'confidence';

export interface WorkLogReviewGroupRow {
  /** Dimension → display value. `null` is preserved (renders as "(none)"). */
  key: Record<string, string | null>;
  count: number;
  hours: number;
  distinctEmployees: number;
  firstSeenAt: string;
  lastSeenAt: string;
  percentOfFiltered: number;
  /** Task 204 D9 — aggregate fused confidence score across the group's
   *  suggestion rows. `null` for groups with no suggestions attached
   *  (e.g. all rows have manual classifications). Optional because
   *  older payloads don't ship the field yet. */
  confidence?: number | null;
}

export interface WorkLogReviewEventRow {
  id: string;
  occurredAt: string;
  employeeId: string | null;
  employeeName: string | null;
  app: string | null;
  appFileName: string | null;
  title: string | null;
  url: string | null;
  site: string | null;
  rootDomain: string | null;
  project: { id: string; name: string; clientId: string; clientName: string } | null;
  classificationSource: ClassificationSource | null;
  classifiedByRuleId: string | null;
  billable: boolean | null;
}

export interface WorkLogReviewResponse {
  filter: {
    from: string;
    to: string;
    classificationSource: ClassificationSource[];
    employeeIds?: string[];
    businessUnitId?: string;
    classifiedByRuleId?: string;
    projectId?: string;
    clientId?: string;
    groupBy: GroupByDimension[];
    sortBy: SortByColumn;
    sortDir: 'asc' | 'desc';
  };
  totals: {
    eventCount: number;
    hours: number;
    distinctEmployees: number;
    distinctGroups: number;
  };
  groups: WorkLogReviewGroupRow[];
  groupCapHit: boolean;
  groupCap: number;
  events: {
    page: number;
    pageSize: number;
    totalCount: number;
    rows: WorkLogReviewEventRow[];
  };
}

/**
 * Inputs for the data hook. Filters that flow into the URL query string.
 * `dateRange` is intentionally absent — the page reads it from `AppContext`
 * (DESIGN-RULES Rule 35).
 */
export interface WorkLogReviewQuery {
  /** ISO date (YYYY-MM-DD). Both ends required when supplied. */
  from?: string;
  to?: string;
  classificationSource?: ClassificationSource[];
  employeeIds?: string[];
  businessUnitId?: string;
  classifiedByRuleId?: string;
  projectId?: string;
  clientId?: string;
  groupBy?: GroupByDimension[];
  sortBy?: SortByColumn;
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}
